// @owners { team: patients-team }
import type { PriceOption } from '@alto/scriptdash/alto/pricing/patients/v3/pricing_endpoint';
import { useMemo } from 'react';
import getCartSelectedPaymentTypes from '~shared/features/checkout/selectors/getSelectedPaymentTypes';
import { pricingInfoForPrescription } from '~shared/features/pricing/selectors/getPricingInfoForPrescription';
import { useOrderPricing } from '~shared/queries';
import { useSelectorShared } from '~shared/store';

type Props = {
  prescriptionID: number;
};

export const useCartSelectedPricingInfoForPrescription = ({ prescriptionID }: Props) => {
  const cartSelectedPaymentTypes = useSelectorShared(getCartSelectedPaymentTypes);
  const selectedPaymentType = cartSelectedPaymentTypes?.[prescriptionID] || null;
  const { orderPricing } = useOrderPricing();

  const prices = useMemo(() => {
    if (!orderPricing?.item_pricing[prescriptionID]) return [];
    return pricingInfoForPrescription(orderPricing.item_pricing[prescriptionID]);
  }, [orderPricing, prescriptionID]);

  const prescriptionPricingInfo = useMemo(() => {
    if (prices.length === 2 && selectedPaymentType) {
      return prices.find((p) => p.paymentType === selectedPaymentType);
    }
    if (prices.length > 0) {
      return prices[0];
    }

    return undefined;
  }, [prices, selectedPaymentType]);

  const savingsAppliedAmount = useMemo(() => {
    const pricing = orderPricing?.item_pricing[prescriptionID];
    if (!pricing) return 0;
    const priceOption = pricing?.prices?.find((price: PriceOption) => price.type === selectedPaymentType);

    return (
      (priceOption?.payment_breakdown?.non_insurance_payers_paid ?? 0) +
      (priceOption?.payment_breakdown?.remainder_paid ?? 0)
    );
  }, [orderPricing, prescriptionID, selectedPaymentType]);

  const priceOptions = useMemo(() => {
    return orderPricing?.item_pricing[prescriptionID]?.prices || [];
  }, [orderPricing, prescriptionID]);

  return { prescriptionPricingInfo, savingsAppliedAmount, priceOptions };
};
